/**
 * TemplateEditing Component
 *
 * Template management interface for email templates with:
 * - ButtonGroup interface for approval, rejection, and waitlist templates
 * - Rich text editor with preview functionality
 * - Template variable hints and validation
 * - Real-time preview with sample data
 * - Save/reset functionality with change tracking
 *
 * This component is refactored from EmailTemplateModal to work as content within EmailManagementModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Eye, Save, RotateCcw, AlertCircle, CheckCircle, XCircle, Clock, Mail, Send, RefreshCcw } from 'lucide-react';

import {
  Input,
  TextArea,
  Card,
  CardHeader,
  CardBody,
  ButtonGroup,
  Divider,
  Chip,
  Spinner,
  Alert,
  Button,
  useToastNotification
} from '@enfomentis/ui';

import { emailTemplateService } from '@/services/EmailTemplateService';
import { emailCategoryService } from '@/services/EmailCategoryService';
import { useAuth } from '@/hooks/useAuth';
import { Brand } from '@enfomentis/shared/config';
import type { EmailTemplate, EmailTemplateType } from '@/types/email';

/**
 * Template editor data for a single template type
 */
interface TemplateEditorData {
  id: string | undefined;
  subject: string;
  content: string;
  hasChanges: boolean;
  isLoading: boolean;
  error: string | null;
}

/**
 * Available template variables for substitution (Beta Application emails)
 */
const BETA_APPLICATION_VARIABLES = [
  { key: '{{applicant_name}}', description: 'Full name of the applicant' },
  { key: '{{application_date}}', description: 'Date the application was submitted' },
  { key: '{{decision_date}}', description: 'Date the application was reviewed/decided' },
  { key: '{{admin_name}}', description: 'Name of the reviewing admin' }
] as const;

/**
 * Available template variables for invitation/activation-reminder emails
 */
const INVITATION_VARIABLES = [
  { key: '{{recipient_name}}', description: 'Full name of the recipient' },
  { key: '{{invitation_link}}', description: 'Secure activation link' },
  { key: '{{invitation_token}}', description: 'Invitation token for manual entry' },
  { key: '{{expires_at}}', description: 'Invitation expiration date' },
  { key: '{{sent_by}}', description: 'Admin who sent the invitation' },
  { key: '{{sent_date}}', description: 'Date the invitation was sent' }
] as const;

/**
 * Get template variables based on email type
 */
const getTemplateVariables = (templateType: EmailTemplateType) => {
  if (templateType === 'invitation' || templateType === 'activation-reminder') {
    return INVITATION_VARIABLES;
  }
  return BETA_APPLICATION_VARIABLES;
};

/**
 * Get default template content for each type
 * Uses Brand config for dynamic brand values
 */
const getDefaultTemplates = (): Record<EmailTemplateType, { subject: string; content: string }> => ({
  approval: {
    subject: `Welcome to ${Brand.displayName} Beta Program!`,
    content: `Dear {{applicant_name}},

Congratulations! We're excited to inform you that your application for the ${Brand.displayName} Beta Program has been approved.

You applied on {{application_date}} and your application was reviewed on {{decision_date}} by {{admin_name}}.

What's next:
• You'll receive access instructions within 24-48 hours
• Join our Discord community for beta testers
• Start exploring ${Brand.displayName}'s features and provide feedback
• Help us improve ${Brand.displayName} with your trading insights

Thank you for your interest in helping us build the future of trading journals.

Best regards,
The ${Brand.displayName} Team

---
This email was sent from the ${Brand.displayName} Beta Application Management System.`
  },
  rejection: {
    subject: `${Brand.displayName} Beta Application Update`,
    content: `Dear {{applicant_name}},

Thank you for your interest in the ${Brand.displayName} Beta Program.

After careful review of your application submitted on {{application_date}}, we regret to inform you that we cannot offer you a spot in our current beta testing cohort.

This decision was made by {{admin_name}} on {{decision_date}} based on our current capacity and testing requirements.

We encourage you to:
• Follow our updates for future beta opportunities
• Join our Discord community for general announcements
• Stay tuned for our public release announcement

We appreciate your interest in ${Brand.displayName} and hope you'll consider trying our platform when it becomes publicly available.

Best regards,
The ${Brand.displayName} Team

---
This email was sent from the ${Brand.displayName} Beta Application Management System.`
  },
  waitlist: {
    subject: `${Brand.displayName} Beta Application - Waitlisted`,
    content: `Dear {{applicant_name}},

Thank you for your application to the ${Brand.displayName} Beta Program submitted on {{application_date}}.

Your application has been reviewed by {{admin_name}} on {{decision_date}} and has been added to our waitlist.

What this means:
• Your application meets our criteria for beta testing
• We've reached capacity for our current cohort
• You'll be considered for future beta waves
• We'll notify you if a spot becomes available

Expected timeline:
• We'll update you within 2-4 weeks
• No action needed from you at this time
• First priority for any openings that become available

Thank you for your patience and continued interest in ${Brand.displayName}.

Best regards,
The ${Brand.displayName} Team

---
This email was sent from the ${Brand.displayName} Beta Application Management System.`
  },
  confirmation: {
    subject: `${Brand.displayName} Beta Application Received!`,
    content: `Dear {{applicant_name}},

Thank you for submitting your application to the ${Brand.displayName} Beta Program on {{application_date}}.

Your application (ID: {{application_id}}) has been successfully received and will be reviewed by our team within 3-5 business days.

What happens next:
• Our team will carefully review your application
• You'll receive an email update once a decision is made
• If selected, you'll receive access instructions and community invitation

We appreciate your interest in ${Brand.displayName} and look forward to potentially having you as part of our beta testing community!

Best regards,
The ${Brand.displayName} Team

---
This email was sent from the ${Brand.displayName} Beta Application Management System.`
  },
  invitation: {
    subject: `Welcome to ${Brand.displayName}! Activate Your Account`,
    content: `Dear {{recipient_name}},

Welcome to the ${Brand.displayName} Beta Program! We're excited to have you join our community.

Your beta application has been approved and you're now ready to activate your account.

Activate Your Account:
Click the link below to set up your password and complete your account activation:
{{invitation_link}}

Your Invitation Details:
• Invitation Token: {{invitation_token}}
• Expires: {{expires_at}}
• Sent by: {{sent_by}}
• Sent on: {{sent_date}}

What's Next:
1. Click the activation link above
2. Set your secure password
3. Complete your profile setup
4. Start exploring ${Brand.displayName} features

This invitation link will expire on {{expires_at}}. If you need a new invitation link, please contact support.

We're thrilled to have you as part of our beta testing community!

Best regards,
The ${Brand.displayName} Team

---
This email was sent from the ${Brand.displayName} Beta Application Management System.`
  },
  'activation-reminder': {
    subject: `Reminder: Activate Your ${Brand.displayName} Account`,
    content: `Dear {{recipient_name}},

This is a friendly reminder that your ${Brand.displayName} account is ready for activation.

We noticed you haven't yet activated your account. Your invitation is still valid and we're excited for you to get started!

Activate Your Account Now:
{{invitation_link}}

Your Invitation Details:
• Invitation Token: {{invitation_token}}
• Expires: {{expires_at}}
• Originally sent by: {{sent_by}}
• Reminder sent on: {{sent_date}}

Why Activate Now?
• Get early access to ${Brand.displayName}'s powerful trading journal features
• Join our active beta testing community
• Provide feedback to shape the final product
• Access exclusive beta tester benefits

Having trouble activating? Contact our support team for assistance.

This invitation link will expire on {{expires_at}}.

Best regards,
The ${Brand.displayName} Team

---
This email was sent from the ${Brand.displayName} Beta Application Management System.`
  }
});

/**
 * Template Editing Component
 *
 * Features:
 * - ButtonGroup interface for approval, rejection, and waitlist templates
 * - Rich text editor with preview functionality
 * - Template variable hints and validation
 * - Real-time preview with sample data
 * - Save/reset functionality with change tracking
 * - Mobile-responsive design
 * - Error handling and loading states
 *
 * Usage:
 * ```tsx
 * <TemplateEditing />
 * ```
 */
export const TemplateEditing: React.FC = () => {
  // Auth context for admin username
  const { user } = useAuth();
  const adminUsername = user?.email || 'Unknown Admin';

  // State management
  const [selectedTab, setSelectedTab] = useState<EmailTemplateType>('approval');
  const [templates, setTemplates] = useState<Record<EmailTemplateType, TemplateEditorData>>({
    approval: { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    rejection: { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    waitlist: { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    confirmation: { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    invitation: { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null },
    'activation-reminder': { id: undefined, subject: '', content: '', hasChanges: false, isLoading: false, error: null }
  });
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [globalLoading, setGlobalLoading] = useState<boolean>(true);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Toast notifications
  const { showToast } = useToastNotification();

  // Load templates on component mount
  const loadTemplates = useCallback(async (): Promise<void> => {
    try {
      setGlobalLoading(true);
      setGlobalError(null);

      const [
        approvalTemplate,
        rejectionTemplate,
        waitlistTemplate,
        confirmationTemplate,
        invitationTemplate,
        activationReminderTemplate
      ] = await Promise.all([
        emailTemplateService.getByType('approval'),
        emailTemplateService.getByType('rejection'),
        emailTemplateService.getByType('waitlist'),
        emailTemplateService.getByType('confirmation'),
        emailTemplateService.getByType('invitation'),
        emailTemplateService.getByType('activation-reminder')
      ]);

      const defaults = getDefaultTemplates();
      setTemplates({
        approval: {
          id: approvalTemplate?.id,
          subject: approvalTemplate?.subject || defaults.approval.subject,
          content: approvalTemplate?.content || defaults.approval.content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        rejection: {
          id: rejectionTemplate?.id,
          subject: rejectionTemplate?.subject || defaults.rejection.subject,
          content: rejectionTemplate?.content || defaults.rejection.content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        waitlist: {
          id: waitlistTemplate?.id,
          subject: waitlistTemplate?.subject || defaults.waitlist.subject,
          content: waitlistTemplate?.content || defaults.waitlist.content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        confirmation: {
          id: confirmationTemplate?.id,
          subject: confirmationTemplate?.subject || defaults.confirmation.subject,
          content: confirmationTemplate?.content || defaults.confirmation.content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        invitation: {
          id: invitationTemplate?.id,
          subject: invitationTemplate?.subject || defaults.invitation.subject,
          content: invitationTemplate?.content || defaults.invitation.content,
          hasChanges: false,
          isLoading: false,
          error: null
        },
        'activation-reminder': {
          id: activationReminderTemplate?.id,
          subject: activationReminderTemplate?.subject || defaults['activation-reminder'].subject,
          content: activationReminderTemplate?.content || defaults['activation-reminder'].content,
          hasChanges: false,
          isLoading: false,
          error: null
        }
      });
    } catch (err) {
      console.error('Failed to load email templates:', err);
      setGlobalError('Failed to load email templates. Please try again.');
    } finally {
      setGlobalLoading(false);
    }
  }, []);

  // Load templates when component mounts
  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Handle template field changes
  const handleTemplateChange = (
    type: EmailTemplateType,
    field: 'subject' | 'content',
    value: string
  ): void => {
    setTemplates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
        hasChanges: true
      }
    }));
  };

  // Handle save template
  const handleSaveTemplate = async (type: EmailTemplateType): Promise<void> => {
    const template = templates[type];
    if (!template.hasChanges) return;

    try {
      setTemplates(prev => ({
        ...prev,
        [type]: { ...prev[type], isLoading: true, error: null }
      }));

      if (!adminUsername || adminUsername === 'Unknown Admin') {
        throw new Error('Admin user not authenticated');
      }

      // Get the Beta Applications category
      const category = await emailCategoryService.getByName('Beta Applications');

      if (!category) {
        throw new Error('Beta Applications category not found');
      }

      const templateData = {
        categoryId: category.id,
        templateType: type,
        subject: template.subject.trim(),
        content: template.content.trim(),
        signature: null,
        updatedBy: adminUsername
      };

      let savedTemplate: EmailTemplate;
      if (template.id) {
        // Update existing template
        savedTemplate = await emailTemplateService.update(template.id, templateData);
      } else {
        // Create new template
        savedTemplate = await emailTemplateService.add(templateData);
      }

      setTemplates(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          id: savedTemplate.id,
          hasChanges: false,
          isLoading: false,
          error: null
        }
      }));

      showToast({
        color: 'success',
        title: 'Template Saved',
        description: `${type.charAt(0).toUpperCase() + type.slice(1)} template saved successfully.`
      });
    } catch (err) {
      console.error(`Failed to save ${type} template:`, err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to save template';

      setTemplates(prev => ({
        ...prev,
        [type]: { ...prev[type], isLoading: false, error: errorMessage }
      }));

      showToast({
        color: 'danger',
        title: 'Save Failed',
        description: `Failed to save ${type} template. Please try again.`
      });
    }
  };

  // Handle reset template to default
  const handleResetTemplate = (type: EmailTemplateType): void => {
    const defaults = getDefaultTemplates();
    setTemplates(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        subject: defaults[type].subject,
        content: defaults[type].content,
        hasChanges: true,
        error: null
      }
    }));
  };

  // Render template variables helper
  const renderVariableHints = (): React.ReactNode => {
    const variables = getTemplateVariables(selectedTab);

    return (
      <Card shadow="sm">
        <CardHeader>
          <h4 className="text-sm font-medium text-text-primary">Available Variables</h4>
        </CardHeader>
        <CardBody className="space-y-2">
          {variables.map((variable) => (
            <div key={variable.key} className="flex items-center justify-between">
              <code className="text-xs bg-default-100 px-2 py-1 rounded">
                {variable.key}
              </code>
              <p className="text-xs text-text-secondary ml-2">{variable.description}</p>
            </div>
          ))}
        </CardBody>
      </Card>
    );
  };

  // Render template preview with sample data
  const renderPreview = (type: EmailTemplateType): React.ReactNode => {
    const template = templates[type];
    const sampleData = {
      '{{applicant_name}}': 'John Doe',
      '{{application_date}}': 'December 15, 2024',
      '{{decision_date}}': 'December 20, 2024',
      '{{admin_name}}': 'Christopher'
    };

    // Replace variables with sample data
    let previewSubject = template.subject;
    let previewContent = template.content;

    Object.entries(sampleData).forEach(([variable, value]) => {
      previewSubject = previewSubject.replace(new RegExp(variable, 'g'), value);
      previewContent = previewContent.replace(new RegExp(variable, 'g'), value);
    });

    return (
      <Card shadow="sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Eye size={16} className="text-primary" />
            <h4 className="text-sm font-medium text-text-primary">Email Preview</h4>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <div>
            <p className="text-xs font-medium text-text-secondary mb-1">Subject:</p>
            <p className="text-sm font-medium text-text-primary">{previewSubject}</p>
          </div>
          <Divider />
          <div>
            <p className="text-xs font-medium text-text-secondary mb-2">Content:</p>
            <div className="bg-default-50 p-3 rounded-md">
              <pre className="text-xs text-text-primary whitespace-pre-wrap font-sans">
                {previewContent}
              </pre>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  };

  // Render template editor
  const renderTemplateEditor = (type: EmailTemplateType): React.ReactNode => {
    const template = templates[type];

    return (
      <div className="space-y-4">
        {/* Template form */}
        <div className="space-y-4">
          <Input
            label="Email Subject"
            value={template.subject}
            onValueChange={(value) => handleTemplateChange(type, 'subject', value)}
            placeholder="Enter email subject line..."
            description="Subject line for the notification email"
            isRequired
          />
          <TextArea
            label="Email Content"
            value={template.content}
            onValueChange={(value) => handleTemplateChange(type, 'content', value)}
            placeholder="Enter email content..."
            description="Email body content. Use variables like {{applicant_name}} for dynamic content."
            minRows={8}
            maxRows={12}
            isRequired
          />
        </div>

        {/* Template error */}
        {template.error && (
          <Alert color="danger" variant="flat">
            <AlertCircle size={16} />
            {template.error}
          </Alert>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            color="primary"
            variant="solid"
            size="sm"
            startContent={<Save size={16} />}
            onPress={() => handleSaveTemplate(type)}
            isLoading={template.isLoading}
            isDisabled={!template.hasChanges || template.isLoading}
          >
            Save Template
          </Button>
          <Button
            color="secondary"
            variant="flat"
            size="sm"
            startContent={<RotateCcw size={16} />}
            onPress={() => handleResetTemplate(type)}
            isDisabled={template.isLoading}
          >
            Reset to Default
          </Button>
          {template.hasChanges && (
            <Chip color="warning" variant="flat" size="sm">
              Unsaved Changes
            </Chip>
          )}
        </div>

        <Divider />

        {/* Preview toggle */}
        <div className="flex items-center gap-2">
          <Button
            color={isPreviewMode ? 'primary' : 'default'}
            variant={isPreviewMode ? 'solid' : 'flat'}
            size="sm"
            startContent={<Eye size={16} />}
            onPress={() => setIsPreviewMode(!isPreviewMode)}
          >
            {isPreviewMode ? 'Hide Preview' : 'Show Preview'}
          </Button>
        </div>

        {/* Preview and variable hints */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isPreviewMode && (
            <div className="lg:col-span-1">
              {renderPreview(type)}
            </div>
          )}
          <div className={isPreviewMode ? 'lg:col-span-1' : 'lg:col-span-2'}>
            {renderVariableHints()}
          </div>
        </div>
      </div>
    );
  };

  // Check if any template has unsaved changes
  const hasUnsavedChanges = Object.values(templates).some(template => template.hasChanges);

  if (globalLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner color="primary" />
        <span className="ml-3 text-text-secondary">Loading templates...</span>
      </div>
    );
  }

  if (globalError) {
    return (
      <div className="text-center py-12">
        <Alert color="danger" variant="flat" className="mb-4">
          <AlertCircle size={16} />
          {globalError}
        </Alert>
        <Button
          color="primary"
          variant="flat"
          onPress={loadTemplates}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <Card shadow="none" radius="none" fullWidth className="h-full flex flex-col">
      {/* Header with Template Type Selection */}
      <CardHeader className="flex-shrink-0 border-b border-default-200">
        <div className="w-full space-y-4">
          <ButtonGroup
            options={[
              {
                label: <span className="hidden sm:inline">Approval</span>,
                value: 'approval',
                icon: CheckCircle
              },
              {
                label: <span className="hidden sm:inline">Rejection</span>,
                value: 'rejection',
                icon: XCircle
              },
              {
                label: <span className="hidden sm:inline">Waitlist</span>,
                value: 'waitlist',
                icon: Clock
              },
              {
                label: <span className="hidden sm:inline">Confirmation</span>,
                value: 'confirmation',
                icon: Mail
              },
              {
                label: <span className="hidden sm:inline">Invitation</span>,
                value: 'invitation',
                icon: Send
              },
              {
                label: <span className="hidden sm:inline">Activation Reminder</span>,
                value: 'activation-reminder',
                icon: RefreshCcw
              }
            ]}
            value={selectedTab}
            onChange={(value) => {
              // Prevent white screen by ensuring we always have a valid value
              if (value && typeof value === 'string' && ['approval', 'rejection', 'waitlist', 'confirmation', 'invitation', 'activation-reminder'].includes(value)) {
                setSelectedTab(value as EmailTemplateType);
              }
            }}
            variant="light"
            color="default"
            size="md"
            radius="none"
            fullWidth
            underlined
          />
          {hasUnsavedChanges && (
            <Chip color="warning" variant="flat" size="sm">
              Unsaved Changes
            </Chip>
          )}
        </div>
      </CardHeader>

      {/* Scrollable Content */}
      <CardBody className="flex-1 overflow-y-auto">
        {renderTemplateEditor(selectedTab)}
      </CardBody>
    </Card>
  );
};